import { Fragment, useState, useEffect } from 'react';
import { Table, TableBody, TableCell, TableHead, TableRow, Stack, TableContainer } from '@mui/material';
import MainCard from 'components/MainCard';
import ScrollX from 'components/ScrollX';
import { EmptyTable, TablePagination } from 'components/third-party/ReactTable';
// third-party
import { Column, useTable, HeaderGroup, Cell, usePagination, useExpanded } from 'react-table';
import { Row } from 'react-table';
import { alpha, useTheme } from '@mui/material/styles';
import { Holding } from 'types/custom/user';
import { Typography } from '@mui/material';
import { Box } from '@mui/material';
import { useSortBy } from 'react-table';

function ReactTable({
  columns,
  data,
  striped,
  emptyMessage,
  pageIndex,
  setPageIndex
}: {
  columns: Column[];
  data: any[];
  striped?: boolean;
  emptyMessage: string;
  setPageIndex: (val: number) => void;
  pageIndex: number;
}) {
  const theme = useTheme();

  const {
    getTableProps,
    getTableBodyProps,
    state: { pageSize },
    headerGroups,
    rows,
    prepareRow,
    page,
    gotoPage,
    setPageSize
  } = useTable(
    {
      columns,
      data,
      initialState: { pageIndex: pageIndex, pageSize: 10, hiddenColumns: ['t1Quantity'] }
    },
    usePagination
  );

  useEffect(() => {
    gotoPage(pageIndex);
  }, [pageIndex, gotoPage]);

  const handleChangePage = (newPage: number) => {
    setPageIndex(newPage);
  };

  return (
    <Table {...getTableProps()} sx={{ border: 'none' }}>
      <TableHead>
        {headerGroups?.map((headerGroup: HeaderGroup<{}>) => (
          <TableRow {...headerGroup.getHeaderGroupProps()} sx={{ '& > th:first-of-type': { width: '58px' } }}>
            {headerGroup?.headers?.map((column: HeaderGroup) => (
              <TableCell sx={{ p: 0.9 }} {...column.getHeaderProps([{ className: column.className }])}>
                {column.render('Header')}
              </TableCell>
            ))}
          </TableRow>
        ))}
      </TableHead>
      <TableBody {...getTableBodyProps()}>
        {page && page?.length > 0 ? (
          page?.map((row: Row, i: number) => {
            prepareRow(row);
            return (
              <Fragment key={i}>
                <TableRow
                  {...row.getRowProps()}
                  sx={{
                    cursor: 'pointer',
                    bgcolor: row.isSelected ? alpha(theme.palette.primary.lighter, 0.35) : 'inherit'
                  }}
                >
                  {row?.cells?.map((cell: Cell) => (
                    <TableCell sx={{ p: 0.8 }} {...cell.getCellProps([{ className: cell.column.className }])}>
                      {cell.render('Cell')}
                    </TableCell>
                  ))}
                </TableRow>
              </Fragment>
            );
          })
        ) : (
          <EmptyTable msg={emptyMessage} colSpan={columns.length ?? 0} />
        )}
        {rows.length > 9 && (
          <TableRow>
            <TableCell colSpan={columns.length} sx={{ p: 0.9 }}>
              <TablePagination
                gotoPage={handleChangePage}
                rows={rows}
                setPageSize={setPageSize}
                pageSize={pageSize}
                pageIndex={pageIndex}
              />
            </TableCell>
          </TableRow>
        )}
      </TableBody>
    </Table>
  );
}

// ReactTable without Pagination
export function ReactTableNoPagination<T extends object>({
  columns,
  data,
  striped,
  emptyMessage,
  getRowSx
}: {
  columns: Column<T>[];
  data: T[];
  striped?: boolean;
  emptyMessage: string;
  getRowSx?: (row: T) => any;
}) {
  const theme = useTheme();

  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } = useTable<T>({
    columns,
    data,
    initialState: { hiddenColumns: ['t1Quantity'] }
  });

  return (
    <Table {...getTableProps()} sx={{ border: 'none' }}>
      <TableHead>
        {headerGroups.map((headerGroup: HeaderGroup<T>) => (
          <TableRow {...headerGroup.getHeaderGroupProps()} sx={{ '& > th:first-of-type': { width: '58px' } }}>
            {headerGroup.headers.map((column: HeaderGroup<T>) => (
              <TableCell sx={{ p: 0.9 }} {...column.getHeaderProps([{ className: column.className }])}>
                {column.render('Header')}
              </TableCell>
            ))}
          </TableRow>
        ))}
      </TableHead>
      <TableBody {...getTableBodyProps()}>
        {rows.length > 0 ? (
          rows.map((row: Row<T>, i: number) => {
            prepareRow(row);
            return (
              <Fragment key={i}>
                <TableRow
                  {...row.getRowProps()}
                  sx={{
                    cursor: 'pointer',
                    bgcolor: row.isSelected ? alpha(theme.palette.primary.lighter, 0.35) : 'inherit',
                    ...(getRowSx ? getRowSx(row.original) : {})
                  }}
                >
                  {row.cells.map((cell: Cell<T>) => (
                    <TableCell sx={{ p: 0.8 }} {...cell.getCellProps([{ className: cell.column.className }])}>
                      {cell.render('Cell')}
                    </TableCell>
                  ))}
                </TableRow>
              </Fragment>
            );
          })
        ) : (
          <EmptyTable msg={emptyMessage} colSpan={columns.length ?? 0} />
        )}
      </TableBody>
    </Table>
  );
}

export const BasicTable = ({
  data,
  striped,
  title,
  columns,
  emptyMessage
}: {
  data: any[];
  striped?: boolean;
  title?: string;
  columns: Column[];
  emptyMessage: string;
}) => {
  const [pageIndex, setPageIndex] = useState<number>(0);
  return (
    <MainCard content={false} title={title}>
      <Stack direction="row" justifyContent="flex-end" alignItems="center" spacing={2}></Stack>
      <ScrollX>
        <ReactTable
          pageIndex={pageIndex}
          setPageIndex={setPageIndex}
          columns={columns}
          data={data}
          striped={striped}
          emptyMessage={emptyMessage}
        />
      </ScrollX>
    </MainCard>
  );
};

export const BasicTableV2 = ({
  data,
  striped,
  title,
  columns,
  emptyMessage,
  actionButtonSection,
  isLoading,
  loadingText
}: {
  data: any[];
  striped?: boolean;
  title?: string;
  columns: Column[];
  emptyMessage: string;
  actionButtonSection?: React.ReactNode;
  isLoading: boolean;
  loadingText: string;
}) => {
  const [pageIndex, setPageIndex] = useState<number>(0);
  return (
    <MainCard content={false}>
      <ScrollX>
        <Stack spacing={3}>
          <Box sx={{ p: 3, pb: 0 }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
              <Typography variant="h5">{title}</Typography>
              {actionButtonSection && actionButtonSection}
            </Stack>
          </Box>

          <ReactTable
            pageIndex={pageIndex}
            setPageIndex={setPageIndex}
            columns={columns}
            data={data}
            striped={striped}
            emptyMessage={isLoading ? loadingText : emptyMessage}
          />
        </Stack>
      </ScrollX>
    </MainCard>
  );
};

export const BasicTableNoPagination = ({
  data,
  striped,
  title,
  columns,
  emptyMessage,
  getRowSx
}: {
  data: any[];
  striped?: boolean;
  title?: string;
  columns: Column[];
  emptyMessage: string;
  getRowSx?: (row: any) => any;
}) => {
  return (
    <MainCard content={false} title={title}>
      <Stack direction="row" justifyContent="flex-end" alignItems="center" spacing={2}></Stack>
      <ScrollX>
        <ReactTableNoPagination columns={columns as any} data={data} striped={striped} emptyMessage={emptyMessage} getRowSx={getRowSx} />
      </ScrollX>
    </MainCard>
  );
};

type Props<T extends object> = {
  data: T[];
  columns: Column<T>[];
  renderRowSubComponent: (row: Row<T>) => JSX.Element;
};

export function ReactTableNoTradeIndicator<T extends object>({ data, columns, renderRowSubComponent }: Props<T>) {
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});

  const { getTableProps, getTableBodyProps, headerGroups, prepareRow, visibleColumns, rows } = useTable<T>(
    {
      columns: columns as Column<T>[],
      data,
      getRowId: (row: any) => row.id,
      // getRowCanExpand: () => true,
      state: { expanded },
      onExpandedChange: setExpanded,

      initialState: {
        pageIndex: 0,
        pageSize: 10
      }
    },
    useSortBy,
    useExpanded
    // usePagination
  );

  return (
    <>
      {/* ===== Table ===== */}
      <Table {...getTableProps()}>
        <TableHead>
          {headerGroups.map((headerGroup: HeaderGroup<T>) => {
            const { key, ...rest } = headerGroup.getHeaderGroupProps();
            return (
              <TableRow key={key} {...rest}>
                {headerGroup.headers.map((column) => {
                  const { key, ...rest } = column.getHeaderProps();
                  return (
                    <TableCell key={key} {...rest} sx={{ p: 0.6 }}>
                      {column.render('Header')}
                    </TableCell>
                  );
                })}
              </TableRow>
            );
          })}
        </TableHead>

        <TableBody {...getTableBodyProps()}>
          {rows.map((row: Row<T>, i: number) => {
            prepareRow(row);
            const { key, ...rowProps } = row.getRowProps();

            return (
              <Fragment key={i}>
                <TableRow
                  {...rowProps}
                  sx={{
                    cursor: 'pointer',
                    bgcolor: !(row.original as any).tradeAllowed ? 'rgba(255, 0, 0, 0.08)' : 'inherit'
                  }}
                  onClick={(e) => {
                    e.stopPropagation();

                    setExpanded((prev) => ({
                      ...prev,
                      [row.id]: !prev[row.id]
                    }));
                  }}
                >
                  {row.cells.map((cell: Cell<T>) => {
                    const { key, ...cellProps } = cell.getCellProps();
                    return (
                      <TableCell key={key} {...cellProps} sx={{ p: 0.6 }}>
                        {cell.render('Cell')}
                      </TableCell>
                    );
                  })}
                </TableRow>

                {expanded[row.id] && (
                  <TableRow>
                    <TableCell colSpan={visibleColumns.length}>{renderRowSubComponent(row)}</TableCell>
                  </TableRow>
                )}
              </Fragment>
            );
          })}

          {/* {data.length > 0 ? (
            <TableRow sx={{ '&:hover': { bgcolor: 'transparent !important' } }}>
              <TableCell colSpan={colSpan} sx={{ p: 1, py: 2 }}>
                <TablePagination
                  gotoPage={gotoPage}
                  rows={rows as any}
                  setPageSize={setPageSize}
                  pageSize={pageSize}
                  pageIndex={pageIndex}
                />
              </TableCell>
            </TableRow>
          ) : (
            <EmptyTable msg="No No-Trade Decisions Found" colSpan={colSpan} />
          )} */}
        </TableBody>
      </Table>
    </>
  );
}

export const BasicTableNoPaginationForHolding = ({
  data,
  title,
  columns,
  emptyMessage,
  onRowClick
}: {
  data: any[];
  title?: string;
  columns: Column[];
  emptyMessage: string;
  onRowClick: (holding: Holding) => void;
}) => {
  return (
    <MainCard content={false} title={title}>
      <Stack direction="row" justifyContent="flex-end" alignItems="center" spacing={2}></Stack>
      <ScrollX>
        <UserHoldingsTableWithModalView columns={columns as any} data={data} emptyMessage={emptyMessage} onRowClick={onRowClick} />
      </ScrollX>
    </MainCard>
  );
};
export function UserHoldingsTableWithModalView<T extends object>({
  columns,
  data,
  emptyMessage,
  onRowClick
}: {
  columns: Column<T>[];
  data: T[];
  emptyMessage: string;
  onRowClick: (holding: Holding) => void;
}) {
  const theme = useTheme();

  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } = useTable<T>({
    columns,
    data,
    initialState: { hiddenColumns: ['t1Quantity'] }
  });

  return (
    <Table {...getTableProps()} sx={{ border: 'none' }}>
      <TableHead>
        {headerGroups.map((headerGroup: HeaderGroup<T>) => (
          <TableRow {...headerGroup.getHeaderGroupProps()} sx={{ '& > th:first-of-type': { width: '58px' } }}>
            {headerGroup.headers.map((column: HeaderGroup<T>) => (
              <TableCell sx={{ p: 0.9 }} {...column.getHeaderProps([{ className: column.className }])}>
                {column.render('Header')}
              </TableCell>
            ))}
          </TableRow>
        ))}
      </TableHead>
      <TableBody {...getTableBodyProps()}>
        {rows.length > 0 ? (
          rows.map((row: Row<T>, i: number) => {
            prepareRow(row);
            return (
              <Fragment key={i}>
                <TableRow
                  {...row.getRowProps()}
                  onClick={(e) => {
                    e.stopPropagation();

                    onRowClick(row.original as Holding);
                  }}
                  sx={{
                    cursor: 'pointer',
                    bgcolor: row.isSelected ? alpha(theme.palette.primary.lighter, 0.35) : 'inherit'
                  }}
                >
                  {row.cells.map((cell: Cell<T>) => (
                    <TableCell sx={{ p: 0.8 }} {...cell.getCellProps([{ className: cell.column.className }])}>
                      {cell.render('Cell')}
                    </TableCell>
                  ))}
                </TableRow>
              </Fragment>
            );
          })
        ) : (
          <EmptyTable msg={emptyMessage} colSpan={columns.length ?? 0} />
        )}
      </TableBody>
    </Table>
  );
}

function AllOrdersTableWithManual({
  columns,
  data,
  striped,
  emptyMessage,
  pageIndex,
  setPageIndex
}: {
  columns: Column[];
  data: any[];
  striped?: boolean;
  emptyMessage: string;
  setPageIndex: (val: number) => void;
  pageIndex: number;
}) {
  const {
    getTableProps,
    getTableBodyProps,
    state: { pageSize },
    headerGroups,
    rows,
    prepareRow,
    page,
    gotoPage,
    setPageSize
  } = useTable(
    {
      columns,
      data,
      initialState: { pageIndex: pageIndex, pageSize: 10, hiddenColumns: ['t1Quantity'] }
    },
    usePagination
  );

  useEffect(() => {
    gotoPage(pageIndex);
  }, [pageIndex, gotoPage]);

  const handleChangePage = (newPage: number) => {
    setPageIndex(newPage);
  };

  return (
    <Table {...getTableProps()} sx={{ border: 'none' }}>
      <TableHead>
        {headerGroups?.map((headerGroup: HeaderGroup<{}>) => (
          <TableRow {...headerGroup.getHeaderGroupProps()} sx={{ '& > th:first-of-type': { width: '58px' } }}>
            {headerGroup?.headers?.map((column: HeaderGroup) => (
              <TableCell sx={{ p: 0.9 }} {...column.getHeaderProps([{ className: column.className }])}>
                {column.render('Header')}
              </TableCell>
            ))}
          </TableRow>
        ))}
      </TableHead>
      <TableBody {...getTableBodyProps()}>
        {page && page?.length > 0 ? (
          page?.map((row: Row, i: number) => {
            prepareRow(row);
            const currentOrder = row.original as any;

            return (
              <Fragment key={i}>
                <TableRow
                  {...row.getRowProps()}
                  sx={{
                    cursor: currentOrder?.manualOrder ? 'pointer' : 'inherit',
                    bgcolor: currentOrder?.manualOrder ? '#FFF9C4' : 'inherit', // Light yellow
                    '&:hover': {
                      bgcolor: currentOrder?.manualOrder ? '#FFF59D !important' : 'inherit' // Important added
                    }
                  }}
                >
                  {row?.cells?.map((cell: Cell) => (
                    <TableCell sx={{ p: 0.8 }} {...cell.getCellProps([{ className: cell.column.className }])}>
                      {cell.render('Cell')}
                    </TableCell>
                  ))}
                </TableRow>
              </Fragment>
            );
          })
        ) : (
          <EmptyTable msg={emptyMessage} colSpan={columns.length ?? 0} />
        )}
        {rows.length > 9 && (
          <TableRow>
            <TableCell colSpan={columns.length} sx={{ p: 0.9 }}>
              <TablePagination
                gotoPage={handleChangePage}
                rows={rows}
                setPageSize={setPageSize}
                pageSize={pageSize}
                pageIndex={pageIndex}
              />
            </TableCell>
          </TableRow>
        )}
      </TableBody>
    </Table>
  );
}

// function AllOrdersTableWithManual<T extends object>({
//   columns,
//   data,
//   emptyMessage
// }: {
//   columns: Column<T>[];
//   data: T[];
//   emptyMessage: string;
// }) {
//   const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } = useTable<T>({
//     columns,
//     data,
//     initialState: { hiddenColumns: ['t1Quantity'] }
//   });

//   return (
//     <Table {...getTableProps()} sx={{ border: 'none' }}>
//       <TableHead>
//         {headerGroups.map((headerGroup: HeaderGroup<T>) => (
//           <TableRow {...headerGroup.getHeaderGroupProps()} sx={{ '& > th:first-of-type': { width: '58px' } }}>
//             {headerGroup.headers.map((column: HeaderGroup<T>) => (
//               <TableCell sx={{ p: 0.9 }} {...column.getHeaderProps([{ className: column.className }])}>
//                 {column.render('Header')}
//               </TableCell>
//             ))}
//           </TableRow>
//         ))}
//       </TableHead>
//       <TableBody {...getTableBodyProps()}>
//         {rows.length > 0 ? (
//           rows.map((row: Row<T>, i: number) => {
//             prepareRow(row);
//             const currentOrder = row.original as any;
//             return (
//               <Fragment key={i}>
//                 <TableRow
//                   {...row.getRowProps()}
//                   sx={{
//                     cursor: currentOrder?.manualOrder ? 'pointer' : 'inherit',
//                     bgcolor: currentOrder?.manualOrder ? '#FFF9C4' : 'inherit', // Light yellow
//                     '&:hover': {
//                       bgcolor: currentOrder?.manualOrder ? '#FFF59D !important' : 'inherit' // Important added
//                     }
//                   }}
//                 >
//                   {row.cells.map((cell: Cell<T>) => (
//                     <TableCell sx={{ p: 0.8 }} {...cell.getCellProps([{ className: cell.column.className }])}>
//                       {cell.render('Cell')}
//                     </TableCell>
//                   ))}
//                 </TableRow>
//               </Fragment>
//             );
//           })
//         ) : (
//           <EmptyTable msg={emptyMessage} colSpan={columns.length ?? 0} />
//         )}
//       </TableBody>
//     </Table>
//   );
// }

export const OrdersTableWithManualOrders = ({
  data,
  title,
  columns,
  emptyMessage
}: {
  data: any[];
  title?: string;
  columns: Column[];
  emptyMessage: string;
}) => {
  const [pageIndex, setPageIndex] = useState<number>(0);

  return (
    <MainCard content={false} title={title}>
      <Stack direction="row" justifyContent="flex-end" alignItems="center" spacing={2}></Stack>
      <ScrollX>
        <AllOrdersTableWithManual
          columns={columns as any}
          data={data}
          emptyMessage={emptyMessage}
          pageIndex={pageIndex}
          setPageIndex={setPageIndex}
        />
      </ScrollX>
    </MainCard>
  );
};

export const RecommendationTable = ({
  data,
  title,
  columns,
  emptyMessage
}: {
  data: any[];
  title?: string;
  columns: Column[];
  emptyMessage: string;
}) => {
  const [pageIndex, setPageIndex] = useState<number>(0);

  return (
    <MainCard content={false} title={title}>
      <Stack direction="row" justifyContent="flex-end" alignItems="center" spacing={2}></Stack>
      <ScrollX>
        <AllOrdersTableWithManual
          columns={columns as any}
          data={data}
          emptyMessage={emptyMessage}
          pageIndex={pageIndex}
          setPageIndex={setPageIndex}
        />
      </ScrollX>
    </MainCard>
  );
};

export function DecisionLogTable<T extends object>({
  columns,
  data,
  emptyMessage,
  pageSize
}: {
  columns: Column<T>[];
  data: T[];
  emptyMessage: string;
  pageSize: number;
}) {
  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } = useTable<T>(
    {
      columns,
      data,
      manualPagination: true,
      initialState: {
        pageSize
      }
    },
    useExpanded
  );

  return (
    <Table {...getTableProps()} sx={{ border: 'none' }}>
      {/* ================= HEADER ================= */}
      <TableHead>
        {headerGroups.map((headerGroup: HeaderGroup<T>) => (
          <TableRow {...headerGroup.getHeaderGroupProps()}>
            {headerGroup.headers.map((column) => (
              <TableCell {...column.getHeaderProps([{ className: column.className }])} sx={{ p: 0.9 }}>
                {column.render('Header')}
              </TableCell>
            ))}
          </TableRow>
        ))}
      </TableHead>

      {/* ================= BODY ================= */}
      <TableBody {...getTableBodyProps()}>
        {rows.length > 0 ? (
          rows.map((row: Row<T>) => {
            prepareRow(row);
            const record: any = row.original;
            const flags = record?.flag;

            return (
              <Fragment key={row.id}>
                {/* ---------- MAIN ROW ---------- */}
                <TableRow
                  {...row.getRowProps()}
                  sx={{
                    cursor: record?.manualOrder ? 'pointer' : 'inherit',
                    bgcolor: record?.manualOrder ? '#FFF9C4' : 'inherit',
                    '&:hover': {
                      bgcolor: record?.manualOrder ? '#FFF59D !important' : 'inherit'
                    }
                  }}
                >
                  {row.cells.map((cell: Cell<T>) => (
                    <TableCell {...cell.getCellProps([{ className: cell.column.className }])} sx={{ p: 0.8 }}>
                      {cell.render('Cell')}
                    </TableCell>
                  ))}
                </TableRow>

                {/* ---------- EXPANDED FLAGS ROW ---------- */}
                {row.isExpanded && flags && (
                  <TableRow>
                    <TableCell
                      colSpan={row.cells.length}
                      sx={{
                        bgcolor: 'grey.50'
                      }}
                    >
                      <Box sx={{ mx: 'auto' }}>
                        <TableContainer>
                          <Table
                            size="small"
                            sx={{
                              border: 1,
                              borderColor: 'divider'
                            }}
                          >
                            <TableHead>
                              <TableRow sx={{}}>
                                <TableCell sx={{ fontWeight: 600 }}>Flag</TableCell>
                                <TableCell sx={{ fontWeight: 600 }}>Value</TableCell>
                              </TableRow>
                            </TableHead>

                            <TableBody sx={{}}>
                              {Object.entries(flags).map(([key, value]) => (
                                <TableRow key={key}>
                                  <TableCell>{key}</TableCell>
                                  <TableCell
                                    sx={{
                                      fontWeight: 600,
                                      color: value ? 'success.main' : 'error.main'
                                    }}
                                  >
                                    {value ? 'True' : 'False'}
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </Box>
                    </TableCell>
                  </TableRow>
                )}
              </Fragment>
            );
          })
        ) : (
          <EmptyTable msg={emptyMessage} colSpan={columns.length} />
        )}
      </TableBody>
    </Table>
  );
}

export function TickCountTable<T extends Object>({
  columns,
  data,
  emptyMessage
}: {
  columns: Column<T>[];
  data: T[];
  emptyMessage: string;
}) {
  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } = useTable({ columns, data });

  return (
    <Table {...getTableProps()} sx={{ border: 'none' }}>
      <TableHead>
        {headerGroups.map((headerGroup: HeaderGroup<T>) => (
          <TableRow {...headerGroup.getHeaderGroupProps()}>
            {headerGroup.headers.map((column) => (
              <TableCell
                {...column.getHeaderProps([{ className: column.className }])}
                // {...(column.canSort ? column.getSortByToggleProps() : {})}
                sx={{ p: 0.9 }}
              >
                {column.render('Header')}
                {/* {column.canSort && <span style={{ marginLeft: 6 }}>{column.isSorted ? (column.isSortedDesc ? '↑' : '↓') : '‒'}</span>} */}
              </TableCell>
            ))}
          </TableRow>
        ))}
      </TableHead>

      <TableBody {...getTableBodyProps()}>
        {rows && rows?.length > 0 ? (
          rows?.map((row, i: number) => {
            prepareRow(row);
            const currentOrder = row.original as any;
            const shouldHighlight = currentOrder.count === 0;

            return (
              <Fragment key={i}>
                <TableRow
                  {...row.getRowProps()}
                  sx={{
                    cursor: currentOrder?.manualOrder ? 'pointer' : 'inherit',

                    backgroundColor: shouldHighlight ? '#FFEBEE' : currentOrder?.manualOrder ? '#FFF9C4' : undefined,

                    '&.MuiTableRow-root:hover': {
                      backgroundColor: shouldHighlight ? '#FFCDD2' : currentOrder?.manualOrder ? '#FFF59D' : undefined
                    }
                  }}
                >
                  {row?.cells?.map((cell: Cell<T>) => (
                    <TableCell sx={{ p: 0.8 }} {...cell.getCellProps([{ className: cell.column.className }])}>
                      {cell.render('Cell')}
                    </TableCell>
                  ))}
                </TableRow>
              </Fragment>
            );
          })
        ) : (
          <EmptyTable msg={emptyMessage} colSpan={columns.length ?? 0} />
        )}
      </TableBody>
    </Table>
  );
}


interface SubscriptionListTableProps {
  columns: Column<any>[];
  data: any[];
  emptyMessage: string;
  pageSize: number;
  handleRowClick: (row: any) => void;
}

export const SubscriptionListTable = ({ columns, data, emptyMessage, pageSize, handleRowClick }: SubscriptionListTableProps) => {
  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow
  } = useTable({ columns, data, initialState: { pageSize } });
  return (
    <Table {...getTableProps()} sx={{}}>
      <TableHead>
        {headerGroups.map((headerGroup: HeaderGroup<any>) => (
          <TableRow {...headerGroup.getHeaderGroupProps()}>
            {headerGroup.headers.map((column) => (
              <TableCell
                {...column.getHeaderProps([{ className: column.className }])}
                sx={{ p: 1.5 }}
              >
                {column.render('Header')}
              </TableCell>
            ))}
          </TableRow>
        ))}
      </TableHead>
      <TableBody {...getTableBodyProps()}>
        {rows && rows?.length > 0 ? (
          rows?.map((row, i: number) => {
            prepareRow(row);
            return (
              <Fragment key={i}>
                <TableRow
                  {...row.getRowProps()}
                  sx={{
                    cursor: 'inherit',
                    borderBottom: '1px solid #E0E0E0',
                    backgroundColor: '#fefefeff',

                    '&.MuiTableRow-root:hover': {
                      backgroundColor: '#f7eff0ff'
                    }
                  }}
                  onClick={() => handleRowClick(row.original)}
                >
                  {row?.cells?.map((cell: Cell<any>) => (
                    <TableCell sx={{ p: 2 }} {...cell.getCellProps([{ className: cell.column.className }])}>
                      {cell.render('Cell')}
                    </TableCell>
                  ))}
                </TableRow>
              </Fragment>
            );
          })
        ) : (
          <EmptyTable msg={emptyMessage} colSpan={columns.length ?? 0} />
        )}
      </TableBody>
    </Table>
  );
}